import { scaleLinear, scaleQuantile, scaleThreshold } from "d3-scale"
import { format } from "date-fns"
import {
  CATEGORICAL_UNITMEASURE,
  DEFAULT_BIN_COLORS,
  EXCLUDED_DISAGGREGATIONS,
  EXCLUDED_DISAGGREGATIONS_INDICATOR_INFO,
  NONE,
  NOT_APPLICABLE,
  PERIODICITY_CODE_PATTERNS,
  TOTAL,
  UNIT_MEASURE_EXCEPTION,
  UNITS,
} from "./constants"
import {
  BinTypes,
  disaggregations,
  LegendType,
  periodicity,
  ScaleTypes,
  mapTypes,
} from "./enum"
import { COLOR_MAP } from "./theme"
import { range } from "d3"

export const getEndpointFromConfig = (endpointList, endpointName) => {
  const endpointObj = endpointList.find(
    (item) => item.endpointLabel === endpointName
  )
  return endpointObj
}

export const fetchDisaggregationList = (
  dimensions,
  isIndicatorInfo = false
) => {
  let disaggregationList = []
  dimensions?.length > 0 &&
    dimensions.forEach((item) => {
      const excludedDisaggregations = isIndicatorInfo
        ? EXCLUDED_DISAGGREGATIONS_INDICATOR_INFO
        : EXCLUDED_DISAGGREGATIONS
      if (
        !excludedDisaggregations.includes(item.field_name) &&
        // item.field_value.filter((value) => value?.id !== NOT_APPLICABLE)
        //   .length > 0
        item.field_value?.length > 1
      ) {
        disaggregationList.push(item)
      }
    })
  return disaggregationList
}

export const calculateGradient = () => {
  const sliderTrack = document.querySelectorAll('.slider-track')

  if (sliderTrack) {
    const sliderOne = document.querySelectorAll('.range1')
    const sliderTwo = document.querySelectorAll('.range2')

    sliderTrack.forEach((slider, index) => {
      const sliderMaxValue = parseInt(sliderOne[index].max)
      const sliderMinValue = parseInt(sliderOne[index].min)
      const sliderValueDifference = sliderMaxValue - sliderMinValue

      const v1 = parseInt(sliderOne[index]?.value) - sliderMinValue
      const v2 = parseInt(sliderTwo[index]?.value) - sliderMinValue

      const percent1 = (v1 / sliderValueDifference) * 100
      const percent2 = (v2 / sliderValueDifference) * 100

      slider.style.background = `linear-gradient(to right, rgba(0, 0, 0, 0.08) ${percent1}%, #0071BC ${percent1}%, #0071BC ${percent2}%, rgba(0, 0, 0, 0.08) ${percent2}%)`
    })
  }
}

export const getCustomDimensionName = (filterValues, filterName) => {
  const values = filterValues.filter(
    (item) => item.code !== NOT_APPLICABLE && item.code !== TOTAL
  )
  let value = ''
  for (let i = 0; i < values.length; i++) {
    value = values[i].description?.split(':')[0]
    if (value) {
      break
    }
  }
  return value ? value : filterName
}

export const getCustomDimensionValue = (filterValue) => {
  if ([TOTAL, NOT_APPLICABLE]?.includes(filterValue?.code)) {
    return filterValue.description
  }
  let value = ''
  if (filterValue?.description?.includes(':')) {
    value = filterValue?.description?.split(':')?.[1]
  }

  return value ? value : filterValue?.description
}

export const getNameFromDisaggregation = (dimensions, type, id) => {
  let name = ''
  const filterData = dimensions?.find(
    (item) => item.field_name?.toUpperCase() === type
  )
  if (
    filterData &&
    filterData.field_value &&
    filterData.field_value.length > 0
  ) {
    const filterValues =
      id &&
      filterData.field_value.find(
        (v) => v.code?.toUpperCase() === id?.toUpperCase()
      )
    name = filterValues ? filterValues.description : ''
  }
  return name
}

export const getBadges = (
  selectedFilters,
  disaggregationData,
  selectedComparison,
  unitMeasureName
) => {
  const dimensions = disaggregationData?.dimensions
  let badges = []
  for (const [key, value] of Object.entries(selectedFilters)) {
    let badge = ''
    const filter = dimensions?.find((item) => item.field_name === key)
    if (filter && selectedComparison === NONE) {
      if (value === TOTAL) {
        badge = filter?.field_name?.startsWith('COMP_BREAKDOWN')
          ? `${getCustomDimensionName(
              filter?.field_value,
              filter.field_name
            )}: ALL`
          : `${
              filter?.label_name?.trim() ? filter.label_name : ''
            }: ALL`.toUpperCase()
      } else {
        const filterValue = filter?.field_value?.find(
          (item) => item.code === value
        )?.description
        if (filter.field_name?.startsWith('COMP_BREAKDOWN')) {
          badge = `${getCustomDimensionName(
            filter.field_value,
            filter.field_name
          )}: ${getCustomDimensionValue(
            filter?.field_value?.find((item) => item.code === value)
          )}`
        } else {
          badge = `${
            filter?.label_name?.trim() ? filter.label_name : ''
          }: ${filterValue}`
        }
      }
    } else if (filter) {
      if (key.toUpperCase() === selectedComparison.toUpperCase()) {
        if (filter.field_name?.startsWith('COMP_BREAKDOWN')) {
          badge = `BY ${getCustomDimensionName(
            filter.field_value,
            filter.field_name
          )}`
        } else {
          badge = `BY ${
            filter?.label_name?.trim() ? filter.label_name : ''
          }`.toUpperCase()
        }
      } else {
        if (value === TOTAL) {
          badge = filter.field_name?.startsWith('COMP_BREAKDOWN')
            ? `${getCustomDimensionName(
                filter.field_value,
                filter.field_name
              )}: ALL`
            : `${
                filter?.label_name?.trim() ? filter.label_name : ''
              }: ALL`.toUpperCase()
        } else {
          const filterValue = filter.field_value?.find(
            (item) => item.code === value
          )?.description
          if (filter.field_name?.startsWith('COMP_BREAKDOWN')) {
            badge = `${getCustomDimensionName(
              filter.field_value,
              filter.field_name
            )}: ${getCustomDimensionValue(
              filter.field_value?.find((item) => item.code === value)
            )}`
          } else {
            badge = `${
              filter?.label_name?.trim() ? filter.label_name : ''
            }: ${filterValue}`
          }
        }
      }
    }

    badges.push(badge)
  }
  if (isMultipleUnitMeasureExist(disaggregationData) && unitMeasureName) {
    badges.push(`Unit Measure: ${unitMeasureName}`)
  }
  return badges
}

export const isCountryInGroup = (countries, countryId) => {
  return countries.some((country) => country.id === countryId)
}

export const clone = (obj) => {
  return obj != null ? JSON.parse(JSON.stringify(obj)) : null
}

export const getDataTableHeaders = (
  combinations,
  disaggregationData,
  indicatorData = null,
  aggregateData = null
) => {
  const headerData = combinations.map((combination, index) => {
    const combinationText =
      Object.entries(combination)
        .map(([key, value]) => {
          if (key.startsWith('COMP_BREAKDOWN')) {
            let customDimensionValue = disaggregationData
              ?.find(
                (dis) =>
                  dis.field_name ===
                  `COMP_BREAKDOWN_${key.charAt(key.length - 1)}`
              )
              ?.field_value?.find((item) => item.code === value)?.description

            if (key !== TOTAL) {
              if (customDimensionValue?.includes(':')) {
                customDimensionValue = customDimensionValue.split(':')?.[1]
              }
            }
            return customDimensionValue
          } else {
            return (
              disaggregationData
                ?.find((dis) => dis.field_name === key)
                ?.field_value?.find((item) => item.code === value)
                ?.description || 'Value'
            )
          }
        })
        .join(', ') || 'Value'

    const data =
      indicatorData && indicatorData.length
        ? indicatorData
        : aggregateData && aggregateData.length
          ? aggregateData
          : []
    const filterSpecificData = data.find((item) => {
      const checkFilter = (filter, value) => {
        if (!filter) return true
        const filterValues = filter.split(',')
        return filterValues.includes(value)
      }

      const passesSelectedFilters = Object.entries(combination).every(
        ([filterKey, filterValue]) => checkFilter(filterValue, item[filterKey])
      )
      return passesSelectedFilters
    })
    let unitMeasureCode =
      filterSpecificData?.[disaggregations.UnitMeasure] || ''
    return { combinationText, unitMeasureCode }
  })
  return headerData
}
export const generateAllCombinations = (filters, limit) => {
  const keys = Object.keys(filters)
  let combinations = [{}]

  keys.forEach((key) => {
    const values = filters?.[key]?.split(',')
    const newCombinations = []

    combinations?.forEach((combination) => {
      values?.forEach((value) => {
        newCombinations.push({ ...combination, [key]: value })
      })
    })

    combinations = newCombinations
  })

  return limit ? combinations?.slice(0, limit) : combinations
}

export const isMultipleUnitMeasureExist = (disaggregationData) => {
  return (
    disaggregationData?.dimensions?.filter(
      (item) =>
        item.field_name === disaggregations.UnitMeasure &&
        item.field_value &&
        item.field_value.length > 1
    )?.length > 0
  )
}

export const isMultipleFrequencyExist = (dimensions) => {
  return (
    dimensions?.filter(
      (item) =>
        item.field_name === disaggregations.Frequency &&
        item.field_value &&
        item.field_value.length > 1
    )?.length > 0
  )
}

export const getDataByDimension = (dimensions, dimension) => {
  const data =
    Array.isArray(dimensions) &&
    dimensions.length > 0 &&
    dimensions?.filter((item) => item.field_name === dimension)
  let values = data && data.length > 0 ? data[0].field_value : []
  return values
}

export const getMappingForBenchmark = (selectedCountries, hierarchyGroups) => {
  let benchmarkList = []
  selectedCountries.forEach((country) => {
    const groups = hierarchyGroups.filter(
      (item) =>
        item.countries?.some((countryObj) => countryObj.id === country) ?? false
    )

    groups.forEach((group) => {
      const benchmarkItem = benchmarkList.find((item) => item.id === group.id)
      if (benchmarkItem) {
        benchmarkItem.countries.push(country)
      } else {
        benchmarkList.push({ id: group.id, countries: [country] })
      }
    })
  })
  return benchmarkList
}

export const formatValues = (value) => {
  let formattedValue = null
  if (!isNaN(value) && value !== undefined && value !== null && value !== '') {
    formattedValue = parseFloat(value)
  }
  return formattedValue
}

export const isValidDataForColorScale = (val) =>
  val !== undefined &&
  val !== 'null' &&
  val !== 'NaN' &&
  val !== null &&
  val !== ''

export const formatCountriesForQueryParams = (countries) => {
  return countries.join(',')
}

export const formatFilterForQueryParams = (data) => {
  const { name, value, selectedComparison, selectedFilters } = data
  let filters = {}
  if (name === 'init') {
    filters = value
  } else {
    if (
      selectedComparison === NONE ||
      name === selectedComparison.toLowerCase() ||
      selectedComparison === ''
    ) {
      filters = {
        ...selectedFilters,
        [name]: value,
      }
    } else {
      filters = {
        ...selectedFilters,
        [name]:
          value.split(',').length > 1
            ? value
            : selectedFilters[name]
              ? selectedFilters[name].split(',').includes(value)
                ? selectedFilters[name]
                    .split(',')
                    .filter((v) => v !== value)
                    .join(',')
                : // : [...state.selectedFilters[name].split(","), value].join(",")
                  value
              : value,
      }
    }
  }
  return filters
}

export const fetchCountryMappingForColor = (
  regionCountriesMapping,
  selectedCountries
) => {
  let mapping = []
  selectedCountries.forEach((country) => {
    const group = regionCountriesMapping.find((item) =>
      isCountryInGroup(item.countries, country)
    )
    if (group) {
      const region = mapping.find((item) => item.id === group.id)
      if (region) {
        region.countries.push(country)
      } else {
        mapping.push({ id: group.id, countries: [country] })
      }
    }
  })
  return mapping
}

const arrayRange = (min, max, binCount) => {
  let range = [min]
  let increment = (max - min) / binCount
  for (let i = 0; i < binCount - 1; i++) {
    let previous = range[range.length - 1]
    let currentValue = previous + increment
    range.push(
      currentValue % 1 === 0 ? currentValue : formatValues(currentValue)
    )
  }
  range.push(max)
  return range
}

export const calculateColorScale = (
  scaleType,
  visualization,
  values,
  data,
  selectedUnitMeasure
) => {
  let legendType = LegendType.Continuous,
    binCount = DEFAULT_BIN_COLORS,
    min = null,
    mid = null,
    max = null,
    customBreaks = null,
    binType = '',
    legendValuesForStd = null
  if (visualization && visualization.legend_type) {
    legendType = visualization.legend_type
  }
  if (
    visualization &&
    visualization.bin_params &&
    visualization.bin_params.length > 0 &&
    visualization.bin_params[0].bin_number &&
    isValidDataForColorScale(visualization.bin_params[0].bin_number)
  ) {
    binCount = parseInt(visualization.bin_params[0].bin_number)
    if (
      (scaleType === ScaleTypes.Sequential ||
        scaleType === ScaleTypes.SequentialReverse ||
        scaleType === ScaleTypes.Sequential_Reverse) &&
      ![3, 4, 5, 6, 7, 9].includes(binCount)
    ) {
      binCount = DEFAULT_BIN_COLORS
    } else if (
      (scaleType === ScaleTypes.Diverging ||
        scaleType === ScaleTypes.DivergingReverse ||
        scaleType === ScaleTypes.Diverging_Reverse) &&
      ![7, 9].includes(binCount)
    ) {
      binCount = DEFAULT_BIN_COLORS
    }
  }
  if (
    visualization &&
    visualization.div_params &&
    visualization.div_params.length > 0
  ) {
    if (isValidDataForColorScale(visualization.div_params[0].min)) {
      min = parseFloat(visualization.div_params[0].min)
    }
    if (isValidDataForColorScale(visualization.div_params[0].mid)) {
      mid = parseFloat(visualization.div_params[0].mid)
    }
    if (isValidDataForColorScale(visualization.div_params[0].max)) {
      max = parseFloat(visualization.div_params[0].max)
    }
  }
  if (
    visualization &&
    visualization.bin_params &&
    visualization.bin_params.length > 0 &&
    visualization.bin_params[0]?.bin_type
  ) {
    binType = visualization.bin_params[0]?.bin_type
    if (visualization.bin_params[0]?.bin_type === BinTypes.Custom) {
      customBreaks = visualization.bin_params[0].custom_breaks.split(',')
    }
  }
  let legendRange = [],
    getColorScale = ''

  if (values.length > 0) {
    if (
      selectedUnitMeasure !== CATEGORICAL_UNITMEASURE &&
      (scaleType === ScaleTypes.Sequential ||
        scaleType === ScaleTypes.Diverging ||
        scaleType === ScaleTypes.SequentialReverse ||
        scaleType === ScaleTypes.DivergingReverse ||
        scaleType === ScaleTypes.Sequential_Reverse ||
        scaleType === ScaleTypes.Diverging_Reverse)
    ) {
      // In diverging scale when only mid is available in metadata without min or max or both:
      // calculate the scale such that the difference between min, mid and max, mid is same
      if (
        (scaleType === ScaleTypes.Diverging ||
          scaleType === ScaleTypes.DivergingReverse ||
          scaleType === ScaleTypes.Diverging_Reverse) &&
        isValidDataForColorScale(mid) &&
        (!isValidDataForColorScale(max) || !isValidDataForColorScale(min))
      ) {
        if (!isValidDataForColorScale(max)) {
          max = Math.max(...values)
        }
        if (!isValidDataForColorScale(min)) {
          min = Math.min(...values)
        }
        // fetch maximum number in absolute values of max-mid and mid-min
        const maxValue = Math.max(
          Math.abs(parseFloat(max) - parseFloat(mid)),
          Math.abs(parseFloat(mid) - parseFloat(min))
        )
        max = parseFloat(mid) + parseFloat(maxValue)
        min = parseFloat(mid) - parseFloat(maxValue)
      } else {
        if (!isValidDataForColorScale(max)) {
          max = Math.max(...values)
        }
        if (!isValidDataForColorScale(min)) {
          min = Math.min(...values)
        }
        if (!isValidDataForColorScale(mid)) {
          mid = (min + max) / 2
        }
      }
      if (legendType === LegendType.Binned) {
        if (
          binType &&
          binType.toLowerCase() === BinTypes.Custom.toLowerCase() &&
          customBreaks
        ) {
          legendRange = customBreaks.map((item) =>
            isValidDataForColorScale(item) ? parseFloat(item) : ''
          )
          getColorScale = scaleQuantile()
            .domain(legendRange)
            .range(COLOR_MAP[`${scaleType.replace(' ', '')}_${binCount}`])
        } else if (
          binType &&
          binType.toLowerCase() === BinTypes.Percentile.toLowerCase()
        ) {
          values.sort((a, b) => a - b)
          getColorScale = scaleQuantile()
            .domain(values)
            .range(COLOR_MAP[`${scaleType.replace(' ', '')}_${binCount}`])
          legendRange = [
            values[0],
            ...scaleQuantile()
              .domain(values)
              .range(range(binCount))
              .quantiles(),
            values[values.length - 1],
          ]
        } else if (
          binType &&
          binType.toLowerCase() === BinTypes.StandardDeviation.toLowerCase()
        ) {
          const mean = values.reduce((a, b) => a + b) / values.length

          const squaredDifferences = values.map((value) =>
            Math.pow(value - mean, 2)
          )
          const variance =
            squaredDifferences.reduce((a, b) => a + b) / values.length

          const standardDeviation = Math.sqrt(variance)
          const difference = 0.5
          if (binCount % 2 === 0) {
            legendRange = [mean]
            legendValuesForStd = []
            for (let i = 1; i <= binCount / 2; i++) {
              // the extreme values are the max and min values of the data if the calculated SD value is less than the max or greater than the min
              if (i === binCount / 2) {
                legendRange.push(
                  max > mean + i * difference * standardDeviation
                    ? max
                    : mean + i * difference * standardDeviation
                )
                legendRange.unshift(
                  min < mean - i * difference * standardDeviation
                    ? min
                    : mean - i * difference * standardDeviation
                )
                 legendValuesForStd.push(
                  `more than ${formatNumberOnUnitMeasure({
                    value: mean + (i - 1) * difference * standardDeviation,
                    unitMeasure: data?.[disaggregations.UnitMeasure],
                    decimals: data?.[disaggregations.Decimals],
                  })} (+ ${(i - 1) * difference} std)`
                )
                legendValuesForStd.unshift(
                  `less than ${formatNumberOnUnitMeasure({
                    value: mean - (i - 1) * difference * standardDeviation,
                    unitMeasure: data?.[disaggregations.UnitMeasure],
                    decimals: data?.[disaggregations.Decimals],
                  })} (- ${(i - 1) * difference} std)`
                )
              } else {
                legendRange.push(mean + i * difference * standardDeviation)
                legendRange.unshift(mean - i * difference * standardDeviation)
                if (i === 1) {
                  legendValuesForStd.push(
                    `${formatNumberOnUnitMeasure({
                      value: mean,
                      unitMeasure: data?.[disaggregations.UnitMeasure],
                      decimals: data?.[disaggregations.Decimals],
                    })} to ${formatNumberOnUnitMeasure({
                      value: mean + i * difference * standardDeviation,
                      unitMeasure: data?.[disaggregations.UnitMeasure],
                      decimals: data?.[disaggregations.Decimals],
                    })} (+ ${i * difference} std)`
                  )
                }
                if (i !== range.length - 1) {
                   legendValuesForStd.push(
                    `${formatNumberOnUnitMeasure({
                      value: mean + i * difference * standardDeviation,
                      unitMeasure: data?.[disaggregations.UnitMeasure],
                      decimals: data?.[disaggregations.Decimals],
                    })} (+ ${i * difference} std) to ${formatNumberOnUnitMeasure(
                      {
                        value: mean + (i + 1) * difference * standardDeviation,
                        unitMeasure: data?.[disaggregations.UnitMeasure],
                        decimals: data?.[disaggregations.Decimals],
                      }
                    )} (+ ${(i + 1) * difference} std)`
                  )
                }
                legendValuesForStd.unshift(
                  `${formatNumberOnUnitMeasure({
                    value: mean - i * difference * standardDeviation,
                    unitMeasure: data?.[disaggregations.UnitMeasure],
                    decimals: data?.[disaggregations.Decimals],
                  })} (- ${i * difference} std) to ${formatNumberOnUnitMeasure({
                    value: mean - (i - 1) * difference * standardDeviation,
                    unitMeasure: data?.[disaggregations.UnitMeasure],
                    decimals: data?.[disaggregations.Decimals],
                  })}${i - 1 === 0 ? '' : ` (- ${(i - 1) * difference} std)`}`
                )
              }
            }
            getColorScale = (value, range) => {
              for (let i = 0; i < range?.length - 1; i++) {
                if (range[i] <= value && value < range[i + 1]) {
                  return COLOR_MAP[`${scaleType.replace(' ', '')}_${binCount}`][
                    i
                  ]
                }
                // if value is equal to the max value in the range
                else if (value === range[range.length - 1]) {
                  return COLOR_MAP[`${scaleType.replace(' ', '')}_${binCount}`][
                    range.length - 2
                  ]
                }
              }
            }
          }
        } else {
          legendRange = arrayRange(min, max, binCount)
          getColorScale = scaleQuantile()
            .domain([min, max])
            .range(COLOR_MAP[`${scaleType.replace(' ', '')}_${binCount}`])
        }
      } else {
        if (min === max) {
          getColorScale = () =>
            COLOR_MAP[`${scaleType.replace(' ', '')}_${binCount}`][0]
        } else {
          const domain = calculateDomain(min, max, binCount)
          getColorScale = scaleLinear()
            .domain(domain)
            .range(COLOR_MAP[`${scaleType.replace(' ', '')}_${binCount}`])
        }
      }
    } else if (
      scaleType === ScaleTypes.Categorical ||
      selectedUnitMeasure === CATEGORICAL_UNITMEASURE
    ) {
      let tempValues = [...values.map((item) => item)]
      let uniqueValues = [...new Set(tempValues)].sort((a, b) =>
        a.localeCompare(b)
      )
      legendRange = [...uniqueValues]
      getColorScale = (value) => {
        const colorIndex = uniqueValues.indexOf('' + value)
        return COLOR_MAP.category[colorIndex]
      }
      return { legendRange, getColorScale }
    }
  }
  return {
    legendType,
    binCount,
    legendRange,
    getColorScale,
    min: formatValues(min),
    mid: formatValues(mid),
    max: formatValues(max),
    binType,
    legendValuesForStd,
  }
}

const calculateDomain = (min, max, binCount) => {
  let domain = [min]
  let increment = (max - min) / (binCount - 1)
  for (let i = 0; i < binCount - 2; i++) {
    let previous = domain[domain.length - 1]
    domain.push(previous + increment)
  }
  domain.push(max)
  return domain
}

export const filterCountriesFromDisaggregation = (
  disaggregationCountries,
  countryList
) => {
  return disaggregationCountries.filter((item) =>
    countryList
      .map((country) => country?.id?.toLowerCase())
      .includes(item?.code?.toLowerCase())
  )
}

export const getAvailableBoundaryTypes = ({
  disaggregationCountries,
  countryList,
  grouping,
  countryHierarchy = null,
}) => {
  const availableBoundaryOptions = []

  if (countryList?.length > 0) {
    availableBoundaryOptions.push(mapTypes.Country)
  }

  const regionCodes = grouping?.['HCL_WB_REGIONS']
    ? grouping['HCL_WB_REGIONS'].map((region) => region.id)
    : []
  const hasRegions = disaggregationCountries.some((item) =>
    regionCodes.includes(item.code)
  )
  if (hasRegions) {
    availableBoundaryOptions.push(mapTypes.Region)
  }

  const continentCodes = countryHierarchy?.CONTINENT
    ? countryHierarchy.CONTINENT.map((continent) => continent.id)
    : []
  const hasContinents = continentCodes.some((continentId) =>
    disaggregationCountries.some((item) => item.code === continentId)
  )
  if (hasContinents) {
    availableBoundaryOptions.push(mapTypes.Continent)
  }

  const incomeGroupCodes = countryHierarchy?.INCOME
    ? countryHierarchy.INCOME.map((income) => income.id)
    : []
  const hasIncomeGroups = incomeGroupCodes.some((incomeId) =>
    disaggregationCountries.some((item) => item.code === incomeId)
  )
  if (countryList?.length > 0 && hasIncomeGroups) {
    availableBoundaryOptions.push(mapTypes.IncomeGroup)
  }

  return availableBoundaryOptions
}

export const fetchSeriesDataWithMarker = (data, isIndicatorPage = true) => {
  let dataWithMarkerOption = data?.map((item, index) => {
    let enabled = isIndicatorPage
    if (
      data?.filter(
        (item) =>
          item !== 'null' &&
          item !== 'NaN' &&
          item !== null &&
          item !== undefined
      ).length === 1
    ) {
      enabled = true
    } else if (!isIndicatorPage && index === data.length - 1) {
      enabled = true
    }

    let seriesData = { y: item }

    seriesData.marker = { enabled: enabled }

    return seriesData
  })
  return dataWithMarkerOption
}

export const createDottedLineZonesForNulls = (data) => {
  const zones = []
  data?.forEach((point, index) => {
    if (
      point !== null &&
      index !== data?.length - 1 &&
      data[index + 1] === null
    ) {
      zones.push({
        value: index + 1,
        dashStyle: 'ShortDash',
      })
    } else if (
      point !== null &&
      index !== data?.length - 1 &&
      data[index + 1] !== null
    ) {
      zones.push({
        value: index + 1,
        dashStyle: 'Solid',
      })
    } else if (point === null) {
      zones.push({
        value: index + 1,
        dashStyle: 'ShortDash',
      })
    }
  })
  return zones
}

export const buildFilterAttributeParams = (dimensions, filters = []) => {
  dimensions?.forEach((dimension) => {
    const { field_name, field_value } = dimension

    if (
      field_name &&
      field_name !== disaggregations.RefArea &&
      field_name !== disaggregations.TimePeriod &&
      field_name !== disaggregations.Region
    ) {
      if (field_value.length > 1) {
        const valueToUse = field_value[0].code
        filters.push({ filterColumn: field_name, filterValue: valueToUse })
      }
    }
  })

  return filters
}

export const formatPeriodicityValues = (
  indicatorPeriodicity,
  timePeriodValues
) => {
  if (!Array.isArray(timePeriodValues) || timePeriodValues.length === 0) {
    return []
  }

  const pattern =
    PERIODICITY_CODE_PATTERNS[indicatorPeriodicity] ||
    PERIODICITY_CODE_PATTERNS[periodicity.Annual]

  const sortedCodes = [
    ...new Set(
      timePeriodValues
        .map((item) => item?.code)
        .filter((code) => typeof code === 'string' && pattern.test(code))
    ),
  ].sort()

  if (indicatorPeriodicity === periodicity.Monthly) {
    return sortedCodes.map((code) => {
      const [year, monthStr] = code.split('-')
      const date = new Date(parseInt(year, 10), parseInt(monthStr, 10) - 1, 1)
      return `${format(date, 'yyyy')}-${format(date, 'MMM').toUpperCase()}`
    })
  }

  return sortedCodes
}
const CHART_SIGNIFICANT_DIGITS = 3
// How many extra digits an axis may add to keep its labels unique
const MAX_EXTRA_DIGITS = 4
// An axis whose values span less than 1% of the largest value drops the k/m/b
const NARROW_RANGE_RATIO = 0.01
const getDecimalPlaces = (shortValue) => (Math.abs(shortValue) >= 100 ? 0 : 2)
const formatCompactValue = (value, extraDigits) => {
  const divider = 10 ** (Math.floor(Math.log10(Math.abs(value)) / 3) * 3)
  const decimalPlaces = getDecimalPlaces(value / divider) + extraDigits
  return Intl.NumberFormat('en', {
    notation: 'compact',
    maximumFractionDigits: decimalPlaces,
    // extra digits are only added to tell two labels apart, so they are padded
    // to keep every label on the axis the same shape
    ...(extraDigits > 0 ? { minimumFractionDigits: decimalPlaces } : {}),
  })
    .format(value)
    .toLowerCase()
}

const formatFullValue = (value, decimalPlaces) =>
  Intl.NumberFormat('en', {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  }).format(value)
  
const formatPercentageForChart = (value, extraDigits) => {
  const absoluteValue = Math.abs(value)
  const fractionDigits =
    (absoluteValue >= 0.1 && absoluteValue <= 10 ? 1 : 2) + extraDigits
  const percentage = Number(value.toFixed(fractionDigits))
  return percentage === 0 ? null : `${percentage}`
}
export const formatNumberForChart = (value, unitMeasure, options = {}) => {
  const { extraDigits = 0, isCompactAllowed = true } = options
  if (value === 0) return '0'
  const absoluteValue = Math.abs(value)

  if (UNIT_MEASURE_EXCEPTION.includes(unitMeasure) && absoluteValue >= 1_000) {
    const unit = [...(UNITS[unitMeasure] ?? [])]
      .reverse()
      .find(({ limit }) => absoluteValue >= limit)
    if (unit) {
      const shortValue = value / unit.factor
      return `${Number(
        shortValue.toFixed(getDecimalPlaces(shortValue) + extraDigits)
      )}${unit.suffix}`
    }
  }

  if (unitMeasure === 'PT' && absoluteValue < 1_000) {
    const percentage = formatPercentageForChart(value, extraDigits)
    if (percentage !== null) return percentage
  }

  if (absoluteValue < 0.001) return toScientificNotation(value, extraDigits)
  if (absoluteValue < 1_000)
    return `${Number(
      value.toPrecision(CHART_SIGNIFICANT_DIGITS + extraDigits)
    )}`
  return isCompactAllowed
    ? formatCompactValue(value, extraDigits)
    : formatFullValue(value, extraDigits)
}
const buildAxisLabels = (tickPositions, unitMeasure) => {
  const formatTicks = (options) =>
    tickPositions.map((tick) =>
      formatNumberForChart(tick, unitMeasure, options)
    )
  const isUnique = (labels) => new Set(labels).size === labels.length

  let labels = formatTicks({})
  if (isUnique(labels)) return labels

  for (let extraDigits = 1; extraDigits <= MAX_EXTRA_DIGITS; extraDigits++) {
    labels = formatTicks({ extraDigits })
    if (isUnique(labels)) return labels
  }

  const largestValue = Math.max(...tickPositions.map(Math.abs))
  const axisRange = Math.max(...tickPositions) - Math.min(...tickPositions)
  if (largestValue > 0 && axisRange / largestValue < NARROW_RANGE_RATIO) {
    for (let extraDigits = 0; extraDigits <= MAX_EXTRA_DIGITS; extraDigits++) {
      labels = formatTicks({ extraDigits, isCompactAllowed: false })
      if (isUnique(labels)) return labels
    }
  }

  const usedLabels = new Set()
  return labels.map((label) => {
    if (usedLabels.has(label)) return ''
    usedLabels.add(label)
    return label
  })
}
const axisLabels = new WeakMap()
const formatAxisValue = (value, unitMeasure, axis) => {
  const tickPositions = axis?.tickPositions
  if (!tickPositions?.length) return formatNumberForChart(value, unitMeasure)

  const axisKey = `${unitMeasure}|${tickPositions.join(',')}`
  let cachedLabels = axisLabels.get(axis)
  if (cachedLabels?.axisKey !== axisKey) {
    cachedLabels = {
      axisKey,
      labels: buildAxisLabels(tickPositions, unitMeasure),
    }
    axisLabels.set(axis, cachedLabels)
  }

  const tickIndex = tickPositions.indexOf(value)
  return tickIndex === -1
    ? formatNumberForChart(value, unitMeasure)
    : cachedLabels.labels[tickIndex]
}

export const formatNumberOnUnitMeasure = (params) => {
  const {
    value,
    unitMeasure,
    decimals,
    isSymbol = true,
    unitType = null,
    isCommaSeparatedFormat = false,
    // Highcharts axis, passed by axis label formatters so that the labels of the
    // same axis can be kept unique
    axis = null,
  } = params

  let res = ''
  const numericValue = Number(value)
  if (!isNaN(numericValue)) {
    const decimalPlaces =
      decimals !== undefined &&
      decimals !== null &&
      !isNaN(decimals) &&
      decimals !== ''
        ? parseInt(decimals, 10)
        : undefined

    if (!isCommaSeparatedFormat) {
      res = axis
        ? formatAxisValue(numericValue, unitMeasure, axis)
        : formatNumberForChart(numericValue, unitMeasure)
    } else if (numericValue !== 0 && numericValue < 1 && numericValue > -1) {
      res = numericValue.toFixed(
        decimalPlaces !== undefined ? decimalPlaces : 2
      )
    } else if (UNIT_MEASURE_EXCEPTION.includes(unitMeasure)) {
      const unitConfig = UNITS[unitMeasure] || []
      for (const { limit, factor, suffix } of unitConfig) {
        if (numericValue >= limit && numericValue < limit * 1_000) {
          res = `${(numericValue / factor).toFixed(
            decimalPlaces !== undefined ? decimalPlaces : 1
          )}${suffix}`
          break
        }
      }
    } else {
      const parsedDecimals = Number.parseInt(decimals, 10)
      const fd = Number.isFinite(parsedDecimals) ? parsedDecimals : 0
      const sd = fd + 3
      res = Intl.NumberFormat('en', {
        notation: 'standard',
        minimumSignificantDigits: sd,
        maximumFractionDigits: fd,
        roundingPriority: 'lessPrecision',
        ...(unitType === 'CUR'
          ? {
              style: 'currency',
              currency: unitMeasure,
            }
          : {}),
      })
        .format(numericValue)
        .toLowerCase()
    }

    // res is empty for a repeated axis label, which should stay empty
    if (isSymbol && res !== '') {
      if (unitMeasure === 'PT') {
        res = res + '%'
      } else if (unitMeasure === 'USD') {
        res = '$' + res
      }
    }
  } else {
    res = value
  }
  return res
}

export const formatUnitMeasure = (unitMeasure, unitMeasureName, shortLabel) => {
  let labelToUse = shortLabel || unitMeasureName

  // For scenarios like WB_WDI_ER_H2O_INTR_PC having unicode characters in unit measure (mU+00B3)
  if (labelToUse && /U\+[0-9a-fA-F]{4}/.test(labelToUse)) {
    labelToUse = labelToUse.replace(/U\+([0-9a-fA-F]{4})/g, (match, hex) => {
      const codePoint = parseInt(hex, 16)
      return String.fromCodePoint(codePoint)
    })
  }

  return unitMeasure &&
    labelToUse &&
    unitMeasure !== 'PT' &&
    unitMeasure !== 'USD' &&
    unitMeasure !== 'U' &&
    unitMeasure !== 'NUMBER' &&
    unitMeasure !== 'COUNT'
    ? ` ${labelToUse}`
    : ''
}

export const formatUnitMult = (disaggregationData, unitMult) => {
  const specialFalsyValues = new Set([
    0,
    '0',
    null,
    'null',
    undefined,
    'undefined',
  ])
  if (!specialFalsyValues.has(unitMult)) {
    const unitMultItems = getDataByDimension(
      disaggregationData?.attributes,
      disaggregations.UnitMult
    )
    const unitMultName =
      unitMultItems?.find((item) => item.code == unitMult)?.description || ''
    return unitMultName
  } else return ''
}

export const getRecentYearRange = (data, hasRecentYear = true) => {
  const years = data
    ?.flatMap((item) => item?.data?.xAxis)
    ?.map((yearStr) => parseInt(yearStr))
    ?.filter((year) => !isNaN(year))

  if (years?.length === 0) {
    return ''
  }

  const minYear = Math.min(...years)
  const maxYear = Math.max(...years)

  return minYear === maxYear
    ? hasRecentYear
      ? `(${minYear})`
      : minYear
    : `(${minYear} - ${maxYear})`
}

export const getSuppressedCountries = (suppression) => {
  let finalSuppressedCountries = []
  // TO-DO: update condition based on suppression datatype in metadata
  // if suppression is array
  if (suppression && Array.isArray(suppression) && suppression.length > 0) {
    finalSuppressedCountries = suppression
  }
  //if suppression is string
  if (typeof suppression === 'string') {
    finalSuppressedCountries = suppression.split(',')
  }
  return finalSuppressedCountries
}

export const applyAxisRange = ({
  visualizationMetadata,
  chartOptions,
  axisKey = 'yAxis',
  axisDimension = 'y',
  scatterPlotlineData = null,
}) => {
  if (!visualizationMetadata && scatterPlotlineData) {
    let allValues = []
    chartOptions.series?.forEach((series) => {
      series?.data?.forEach((point) => {
        let val
        if (typeof point === 'object' && point !== null) {
          val = point[axisDimension]
        } else {
          val = point
        }

        if (val !== null && !isNaN(val)) {
          allValues.push(Number(val))
        }
      })
    })
    // calculate scatter plot max values considering plotline data also
    let dataMin = Math.min(
      ...allValues,
      ...scatterPlotlineData.map((pl) => pl.value)
    )
    let dataMax = Math.max(
      ...allValues,
      ...scatterPlotlineData.map((pl) => pl.value)
    )
    chartOptions[axisKey].min = dataMin
    chartOptions[axisKey].max = dataMax
  } else if (!visualizationMetadata) {
    return
  } else if (visualizationMetadata) {
    let abs_min = visualizationMetadata.axis_type_params?.[0]?.abs_min
    let abs_max = visualizationMetadata.axis_type_params?.[0]?.abs_max
    if (visualizationMetadata.axis_type === 'fix' && abs_min && abs_max) {
      chartOptions[axisKey].min = Number(abs_min)
      chartOptions[axisKey].max = Number(abs_max)
    } else {
      let allValues = []
      chartOptions.series?.forEach((series) => {
        series?.data?.forEach((point) => {
          let val
          if (typeof point === 'object' && point !== null) {
            val = point[axisDimension]
          } else {
            val = point
          }

          if (val !== null && !isNaN(val)) {
            allValues.push(Number(val))
          }
        })
      })
      // if scatter chart, show chart even if no country data but aggregates available
      if (!scatterPlotlineData && !allValues.length) return

      let dataMin = scatterPlotlineData
        ? allValues.length
          ? Math.min(...allValues)
          : null
        : Math.min(...allValues)
      let dataMax = scatterPlotlineData
        ? allValues.length
          ? Math.max(...allValues)
          : null
        : Math.max(...allValues)
      // calculate scatter plot max values considering plotline data also
      if (scatterPlotlineData) {
        dataMin =
          dataMin !== null
            ? Math.min(...scatterPlotlineData.map((pl) => pl.value), dataMin)
            : Math.min(...scatterPlotlineData.map((pl) => pl.value))
        dataMax =
          dataMax !== null
            ? Math.max(...scatterPlotlineData.map((pl) => pl.value), dataMax)
            : Math.max(...scatterPlotlineData.map((pl) => pl.value))
      }
      // when only 1 data point available
      if (dataMin === dataMax) {
        // condition if both abs_min, abs_max are available in metadata
        if (abs_min && abs_max) {
          // check if the data point is within the abs_min, abs_max range
          if (Number(abs_min) <= dataMin && dataMin <= Number(abs_max)) {
            dataMin = dataMin
          } else {
            dataMin = Number(abs_min)
            dataMax = Number(abs_max)
          }
        } else if (abs_min && !abs_max) {
          dataMin = Number(abs_min) <= dataMin ? dataMin : Number(abs_min)
        } else if (!abs_min && abs_max) {
          dataMin = Number(abs_max) >= dataMin ? dataMin : Number(abs_max)
        }
      } else {
        if (abs_min) {
          dataMin = Math.max(dataMin, Number(abs_min))
          dataMin =
            abs_max && dataMin > Number(abs_max) ? Number(abs_min) : dataMin
        }
        if (abs_max) {
          dataMax = Math.min(dataMax, Number(abs_max))
          dataMax =
            abs_min && dataMax < Number(abs_min) ? Number(abs_max) : dataMax
        }
      }
      chartOptions[axisKey].min = dataMin
      chartOptions[axisKey].max = dataMax
    }
  }
}

export const parseColorToRGB = (color, noDataColor) => {
  if (color.startsWith('rgb')) {
    const [r, g, b] = color.match(/\d+/g).map(Number)
    return { r, g, b }
  } else if (color.startsWith('#')) {
    const r = parseInt(color.slice(1, 3), 16)
    const g = parseInt(color.slice(3, 5), 16)
    const b = parseInt(color.slice(5, 7), 16)
    return { r, g, b }
  } else {
    return parseColorToRGB(noDataColor)
  }
}

export const getAPIHeaders = ({apiKeyName, apiKeyValue }) => {
  return apiKeyName ? { [apiKeyName]: apiKeyValue } : undefined
}

export const formatFiltersForDataAPI = (filter) => {
  return encodeURIComponent(JSON.stringify(filter))
}
export const isNumeric = (value) => {
  return value !== null && !isNaN(value)
}
export const getUnitMeasureItems = (disaggregationData) => {
  const itemsFromDimensions = getDataByDimension(
    disaggregationData?.dimensions,
    disaggregations.UnitMeasure
  )
  return itemsFromDimensions?.length > 0
    ? itemsFromDimensions
    : getDataByDimension(
        disaggregationData?.attributes,
        disaggregations.UnitMeasure
      )
}

export const getBadgeForDataTable = (
  selectedFilters,
  disaggregationData,
  unitMeasureName
) => {
  const dimensions = disaggregationData?.dimensions
  let badges = []
  for (const [key, value] of Object.entries(selectedFilters)) {
    const filter = dimensions?.filter((item) => item.field_name === key)

    let badgeName = filter[0]?.label_name?.trim() ? filter[0].label_name : ''
    let badgeValue = ''
    let values = value?.split(',')
    if (filter[0]?.field_name?.startsWith('COMP_BREAKDOWN')) {
      badgeName = getCustomDimensionName(
        filter[0].field_value,
        filter[0].field_name
      )
      values?.forEach((item) => {
        const filterValue = getCustomDimensionValue(
          filter[0].field_value.find((filterValue) => filterValue.code === item)
        )
        badgeValue = badgeValue ? `${badgeValue}, ${filterValue}` : filterValue
      })
    } else {
      values?.forEach((item) => {
        const filterValue = filter[0].field_value.filter(
          (filterValue) => filterValue.code === item
        )[0]?.description
        badgeValue = badgeValue ? `${badgeValue}, ${filterValue}` : filterValue
      })
    }
    const badge = `${badgeName}: ${badgeValue}`
    badges.push(badge)
  }
  if (isMultipleUnitMeasureExist(disaggregationData) && unitMeasureName) {
    badges.push(`Unit Measure: ${unitMeasureName}`)
  }
  return badges
}

export const getBadgesElement = (badges) => {
  return badges && badges.length > 0
    ? badges.map((badge) => (
        <div key={badge} className="text tui_badge">
          <div>
            <p>{badge}</p>
          </div>
        </div>
      ))
    : []
}
export const getMaxYearFromDimensions = (dimensions) => {
  const timePeriodFilter = dimensions?.find(
    (item) => item.field_name === disaggregations.TimePeriod
  )
  const years = timePeriodFilter?.field_value
    ?.map((item) => parseInt(item.code))
    ?.sort()
  return years?.length ? years[years.length - 1] : null
}
export const isValueOutOfRangeForRelAxis = ({
  axisType,
  abs_min,
  abs_max,
  value,
}) => {
  return (
    axisType === 'rel' &&
    ((abs_min && formatValues(value) < Number(abs_min)) ||
      (abs_max && formatValues(value) > Number(abs_max)))
  )
}
